import * as React from 'react';

import {
    Metadata,
    ResourceRetentionPolicySummary,
    Submission,
    SubmissionRequestSummary,
    PortalTextCommon,
    PortalTextSubmissionDetails,
} from '../../Models/Api/strongbox.financialportal';

import {
    Tooltip
} from '@mui/material';

import { SubmissionVerification } from '../SubmissionVerification/SubmissionVerification';

import { StringFormat } from '../../Utils/StringUtils';
import { formatStringMDYFullMonth, FormatDate } from '../../Utils/DateUtils';
import { InlineStylingValues } from '../../Utils/Constants';
import { RequestSummary } from './RequestSummary';

import { ConfirmModal, ConfirmModalType } from '../ConfirmModal/ConfirmModal';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTrash,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';

type Props = {
    loadedUserDisplayName?: string;
    submissionDetailsText?: PortalTextSubmissionDetails;
    uploadDate: string;
    submittedBy: string | undefined;
    submissionRequestSummary?: SubmissionRequestSummary;
    submission?: Submission;
    commonText?: PortalTextCommon;
    retentionPolicySummary?: ResourceRetentionPolicySummary;
    submissionMetadata?: Metadata[];
    onDeleteSubmission: (submissionId: string) => void;
}

export const FileGroupSubmissionDetailsSummary: React.FC<Props> = (props): React.ReactElement => {
    const {
        commonText,
        loadedUserDisplayName,
        retentionPolicySummary,
        submission,
        submissionDetailsText,
        submittedBy,
        submissionMetadata,
        submissionRequestSummary,
        onDeleteSubmission,
    } = props;

    // If a submission is scheduled for automatic deletion, we'll show it as being in
    // a warning state if the number of seconds remaining to deletion is less than 
    // deletionDateWarning.  86400 is the number of seconds in a day.
    const deletionDateWarning = (86400 * 7);

    const [askDelete, setAskDelete] = React.useState<string | undefined>(undefined);

    const policySummaryContent = (): React.ReactElement => {
        // shouldn't be called if this is undefined but sanity check.
        if (!(retentionPolicySummary && retentionPolicySummary.autoDeleteAt)) {
            return (<></>);
        }

        const deleteDate = new Date(retentionPolicySummary.autoDeleteAt);

        if (retentionPolicySummary.isDeleted) {
            return (
                <div className={'header-row'} style={{ marginTop: '0.75em' }}>
                    <h3 className={'auto-delete-deleted'}>
                        {StringFormat(submissionDetailsText?.templateSubmissionAutomaticallyDeleted || '{0}', FormatDate(deleteDate, formatStringMDYFullMonth))}
                    </h3>
                </div>
            );
        }

        const currentDate = new Date();

        // getTime returns milliseconds from epoch.  deltaToDelete will be negative if
        // somehow current date is greater than the deletion date.  Would expect it to be
        // less if we get here.
        const deltaToDelete = (deleteDate.getTime() - currentDate.getTime()) / 1000;

        let dateClass = 'auto-delete';

        if ((deltaToDelete >= 0) && (deltaToDelete < deletionDateWarning)) {
            dateClass += ' auto-delete-warning';
        }

        return (
            <div className={'header-row'} style={{ marginTop: '0.75em' }}>
                <h3>{submissionDetailsText?.scheduledForDeletionOn || ''} <span className={dateClass}>{FormatDate(deleteDate, formatStringMDYFullMonth)}</span></h3>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className={'header-row'}>
                <div className={'header-content'}>
                    <h2 style={{ marginTop: '8px' }}>{submissionDetailsText?.collectionDetails || ''}</h2>
                </div>
                <div className={'header-content'}>
                    {
                        //If we have an email for who was the actual submitter of this submission (see the comments above
                        // for props.submittedBy) and from a high level if the request came from a shareable link, then
                        // we show the submitter's email.  If there's a request summary then it's a shareable link if 
                        // hasShareableLink is true.  If there's no request summary, it happened a long time ago and we
                        // will assume that if there's an email for who submitted it that it was in fact a shareable link
                        !!submittedBy &&
                        ((!!submissionRequestSummary && submissionRequestSummary.hasShareableLink) ||
                            (!submissionRequestSummary)) &&
                        (
                            <h3 
                                style={{ 
                                    marginLeft: InlineStylingValues.BasicTextSeparator, 
                                    marginRight: InlineStylingValues.BasicTextSeparator,
                                    marginTop: '8px' 
                                }}
                            >
                                <strong>{submissionDetailsText?.submittersEmail || ''}</strong> {submittedBy}
                            </h3>
                        )
                    }
                    {
                        !!submission && (
                            <Tooltip title={submissionDetailsText?.hideSubmission || ''}>
                                <FontAwesomeIcon 
                                    icon={faTrash} 
                                    onClick={() => setAskDelete(submission!.id)}                                    
                                />
                            </Tooltip>
                        )
                    }
                    <SubmissionVerification 
                        submission={submission} 
                        style={{ marginLeft: '24px' }} 
                        commonText={commonText}
                    />
                </div>
            </div>
            { retentionPolicySummary && policySummaryContent() }
            <RequestSummary
                requestSummary={submissionRequestSummary}
                loadedUserDisplayName={loadedUserDisplayName}
                submission={submission}
                submissionMetadata={submissionMetadata}
            />
            {
                !!askDelete && (
                    <ConfirmModal
                        msg={(
                            <div>
                                <p>{submissionDetailsText?.deleteSubmissionQuestion1 || ''}</p>
                                <p>{submissionDetailsText?.deleteSubmissionQuestion2 || ''}</p>
                            </div>
                        )}
                        title={'Warning'}
                        modalType={ConfirmModalType.yesno}
                        onTerminalButton={(yes?: boolean) => {
                            if (!!yes) {
                                onDeleteSubmission(askDelete);
                            }
                            setAskDelete(undefined);
                        }}
                        commonText={props.commonText}
                    />
                )
            }
        </React.Fragment>
    );
}
